/**
 * Global locale management without dependencies on Inertia or Vue
 *
 * This module provides a way to access the current locale from anywhere
 * in the application without creating dependencies on framework-specific
 * utilities like usePage() from Inertia.
 */

// Default locale fallback
const DEFAULT_LOCALE = 'cs';

// Global locale store
let currentLocale = DEFAULT_LOCALE;

/**
 * Get the current application locale
 * Falls back to 'cs' if no locale is set
 */
export const getCurrentLocale = (): string => {
    return currentLocale || DEFAULT_LOCALE;
};

/**
 * Set the current application locale
 * This should be called by the application initialization
 * or when locale changes
 */
export const setCurrentLocale = (locale: string): void => {
    currentLocale = locale;
};

/**
 * Initialize locale from various sources
 * Can be called from app initialization with Inertia props
 */
export const initializeLocale = (locale?: string): void => {
    if (locale) {
        setCurrentLocale(locale);
        return;
    }

    // Try to get from browser if available
    if (typeof window !== 'undefined') {
        // Try to get from document lang attribute
        const documentLang = document.documentElement.lang;
        if (documentLang) {
            setCurrentLocale(documentLang);
            return;
        }

        // Try to get from browser language
        const browserLang = navigator.language.split('-')[0];
        if (browserLang) {
            setCurrentLocale(browserLang);
            return;
        }
    }

    // Fallback to default
    setCurrentLocale(DEFAULT_LOCALE);
};

/**
 * Check if a locale is supported by the application
 */
export const isValidLocale = (locale: string): boolean => {
    const supportedLocales = ['cs', 'en', 'sk'];
    return supportedLocales.includes(locale);
};

/**
 * Get supported locales
 */
export const getSupportedLocales = (): string[] => {
    return ['cs', 'en', 'sk'];
};
